'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { useAuth } from '@/components/providers/auth-provider'
import { useRouter } from 'next/navigation'
import { SiteHeader, SiteFooter, AnimatedHero } from '@/app/page'

export default function LandingPageEn() {
  const { user, loading } = useAuth()
  const router = useRouter()

  useEffect(() => {
    if (!loading && user) {
      router.replace('/dashboard/invoices/quick')
    }
  }, [user, loading, router])

  return (
    <div className="bg-[#EBEBEB] min-h-screen" style={{ fontFamily: 'Inter, sans-serif' }}>
      <AnimatedHero>
        <SiteHeader lang="en" />

        <div className="flex items-center justify-center min-h-screen px-6 md:px-12 pt-32 pb-20">
          <div className="max-w-3xl text-center">
            <div className="inline-block mb-8 px-4 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ background: '#111', color: '#fff' }}>
              Invitation only
            </div>
            <h1 className="text-[44px] md:text-[72px] leading-[1.05] mb-8 tracking-tight" style={{ fontWeight: 300, color: '#111', letterSpacing: '-0.02em' }}>
              Invoicing & customer<br /><strong style={{ fontWeight: 700 }}>management, curated.</strong>
            </h1>
            <p className="text-[17px] md:text-[20px] text-gray-700 max-w-2xl mx-auto leading-relaxed mb-10" style={{ fontWeight: 300 }}>
              Built for Estonian small businesses who value simplicity, quality, and personal service.
              Every customer is welcomed individually.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link href="/en/apply" className="inline-flex items-center px-7 py-3.5 rounded-full text-[14px] font-semibold transition-all hover:scale-[1.02]" style={{ background: '#111', color: '#fff' }}>
                Apply for access →
              </Link>
              <Link href="/sisene" className="inline-flex items-center px-7 py-3.5 rounded-full text-[14px] font-medium transition-colors hover:bg-black/5" style={{ color: '#111' }}>
                I have an account
              </Link>
            </div>
          </div>
        </div>
      </AnimatedHero>

      {/* WHAT WE DO */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 mb-4">What we do</div>
          <h2 className="text-[36px] md:text-[56px] leading-[1.1] mb-16 tracking-tight" style={{ fontWeight: 300, color: '#111', letterSpacing: '-0.02em' }}>
            Simple, quietly<br /><strong style={{ fontWeight: 700 }}>done right.</strong>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { title: 'Invoicing', text: 'Fast invoice creation, customer and service management in one place. Bank transfers automatically reconciled with invoices.' },
              { title: 'Recurring invoices', text: 'Monthly subscriptions invoice themselves. You stay in control while technology handles the repetitive work.' },
              { title: 'Customer stories', text: 'Not a table — relationships. Each customer carries their full history: invoices, payments, notes.' },
              { title: 'Reports & tracking', text: 'A clear picture of your business health. Cash flow, debtors, forecasts — always one click away.' },
            ].map((f, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 md:p-10">
                <div className="w-10 h-10 rounded-full mb-6 flex items-center justify-center" style={{ background: '#F9C8C8' }}>
                  <span style={{ color: '#111', fontWeight: 700, fontSize: '14px' }}>{i + 1}</span>
                </div>
                <h3 className="text-[20px] font-semibold mb-3 tracking-tight" style={{ color: '#111' }}>{f.title}</h3>
                <p className="text-[15px] text-gray-600 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR WHO */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 mb-4">Who it's for</div>
          <h2 className="text-[36px] md:text-[56px] leading-[1.1] mb-10 tracking-tight" style={{ fontWeight: 300, color: '#111', letterSpacing: '-0.02em' }}>
            Not for<br /><strong style={{ fontWeight: 700 }}>everyone.</strong>
          </h2>
          <p className="text-[17px] md:text-[20px] text-gray-600 leading-relaxed mb-6" style={{ fontWeight: 300 }}>
            Sassu is not a mass-market product. We accept every customer personally to make sure
            our service truly fits your needs.
          </p>
          <p className="text-[17px] md:text-[20px] text-gray-600 leading-relaxed mb-12" style={{ fontWeight: 300 }}>
            If you're a small business owner whose time is worth more than the cheapest alternative —
            get in touch.
          </p>
          <Link href="/en/apply" className="inline-flex items-center px-7 py-3.5 rounded-full text-[14px] font-semibold transition-all hover:scale-[1.02]" style={{ background: '#F9C8C8', color: '#111' }}>
            Tell us about yourself →
          </Link>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="py-24 md:py-32 px-6 md:px-12" style={{ background: '#EBEBEB' }}>
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 mb-4">Joining</div>
          <h2 className="text-[36px] md:text-[56px] leading-[1.1] mb-8 tracking-tight" style={{ fontWeight: 300, color: '#111', letterSpacing: '-0.02em' }}>
            One form —<br /><strong style={{ fontWeight: 700 }}>one conversation.</strong>
          </h2>
          <p className="text-[16px] md:text-[18px] text-gray-600 leading-relaxed mb-10 max-w-xl mx-auto" style={{ fontWeight: 300 }}>
            We don't publish phone numbers or email addresses. All communication starts through the
            application form — that way we connect the right people at the right time.
          </p>
          <Link href="/en/apply" className="inline-flex items-center px-7 py-3.5 rounded-full text-[14px] font-semibold transition-all hover:scale-[1.02]" style={{ background: '#111', color: '#fff' }}>
            Submit application →
          </Link>
        </div>
      </section>

      <SiteFooter lang="en" />
    </div>
  )
}
